import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { inventoriesApi } from '@/api/clients/inventoriesApi'
import type { CreateInventoryRequest, Inventory } from '@/api/types'
import { PageHeader, Card, Button } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { QueryState } from '@/components/common/State'
import { InventoryForm } from '@/components/inventory/InventoryForm'
import { formatDate } from '@/components/common/format'

// Criticality → badge tint (critical=red … low=neutral); readable in light + dark.
const CRITICALITY_VARIANT: Record<string, 'error' | 'warning' | 'info' | 'neutral'> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'neutral',
}

export default function Inventories() {
  const { currentOrgId: orgId } = useOrgStore()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Inventory | null>(null)

  const inventories = useQuery({
    queryKey: ['inventories', orgId],
    queryFn: () => inventoriesApi.list(orgId),
    enabled: !!orgId,
  })

  return (
    <div>
      <PageHeader
        title="Inventories"
        subtitle="Business systems — the primary risk aggregation unit"
        actions={
          <Button variant="primary" icon="add" onClick={() => setShowCreate(true)}>
            New inventory
          </Button>
        }
      />

      <Card className="!p-0">
        <QueryState
          isLoading={inventories.isLoading}
          error={inventories.error}
          isEmpty={inventories.data?.length === 0}
          emptyProps={{
            icon: 'inventory_2',
            title: 'No inventories yet',
            hint: 'Create an inventory, then add sources and ingest a CycloneDX/CBOM to score assets.',
            action: (
              <Button variant="primary" icon="add" onClick={() => setShowCreate(true)}>
                New inventory
              </Button>
            ),
          }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Business unit</th>
                <th className="px-5 py-3 font-medium">Criticality</th>
                <th className="px-5 py-3 font-medium">Reg. scope</th>
                <th className="px-5 py-3 font-medium">Origin</th>
                <th className="px-5 py-3 font-medium">Updated</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {inventories.data?.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => navigate(`/inventories/${inv.id}`)}
                  className="cursor-pointer hover:bg-primary/5"
                >
                  <td className="px-5 py-3 font-medium text-text-light dark:text-text-dark">
                    {inv.name}
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                    {inv.business_unit || '—'}
                  </td>
                  <td className="px-5 py-3">
                    {inv.criticality ? (
                      <Badge label={inv.criticality} variant={CRITICALITY_VARIANT[inv.criticality] ?? 'neutral'} />
                    ) : (
                      <span className="text-subtle-light dark:text-subtle-dark">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge label={inv.regulatory_scope || 'unscoped'} variant="info" />
                  </td>
                  <td className="px-5 py-3 capitalize text-subtle-light dark:text-subtle-dark">
                    {inv.origin}
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                    {formatDate(inv.updated_at)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button
                      icon="delete"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(inv)
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>

      {showCreate && (
        <CreateInventoryModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['inventories', orgId] })
            setShowCreate(false)
            navigate(`/inventories/${id}`)
          }}
          orgId={orgId}
        />
      )}

      {deleteTarget && (
        <DeleteInventoryModal
          orgId={orgId}
          inv={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ['inventories', orgId] })
            qc.invalidateQueries({ queryKey: ['summary', orgId] })
            setDeleteTarget(null)
          }}
        />
      )}
    </div>
  )
}

function DeleteInventoryModal({
  orgId,
  inv,
  onClose,
  onDeleted,
}: {
  orgId: string
  inv: Inventory
  onClose: () => void
  onDeleted: () => void
}) {
  const mutation = useMutation({
    mutationFn: () => inventoriesApi.remove(orgId, inv.id),
    onSuccess: onDeleted,
  })

  return (
    <Modal
      title={`Remove inventory "${inv.name}"`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-text-light dark:text-text-dark">
        <p>
          This permanently deletes the inventory and its sources, assets, and scores. This cannot
          be undone.
        </p>
        {inv.origin === 'discovery' && (
          <div className="p-3 rounded-xl bg-warning/10 text-xs">
            <span className="material-icons text-warning text-sm align-middle mr-1">warning</span>
            This inventory is mirrored from qtz-discovery — it will reappear on the next
            "Sync data" unless it is also removed in discovery.
          </div>
        )}
        {mutation.error && <p className="text-error">{(mutation.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function CreateInventoryModal({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const [form, setForm] = useState<CreateInventoryRequest>({ name: '' })

  const mutation = useMutation({
    mutationFn: () => inventoriesApi.create(orgId, form),
    onSuccess: (inv) => onCreated(inv.id),
  })

  return (
    <Modal
      title="New inventory"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <InventoryForm onChange={setForm} />
      {mutation.error && (
        <p className="mt-3 text-sm text-error">{(mutation.error as Error).message}</p>
      )}
    </Modal>
  )
}
